import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Stack } from 'src/components/Stack';
import { Typography } from 'src/components/Typography';
import { ClientSearchResult } from 'src/models/user/_types';
import { Visit } from 'src/models/visit/_types';
import { ClientAutocomplete } from 'src/shared/ClientAutocomplete';

import { PendingVisitRow } from './PendingVisitRow';
import { VisitRow } from './VisitRow';
import { PendingVisit } from './_types';

import type { Appointment } from 'src/models/appointment/_types';

interface VisitsSectionProps {
  appointment: Appointment;
  canManage: boolean;
  pendingVisits: PendingVisit[];
  onAddClient?: (client: ClientSearchResult) => void;
  onSavePending?: (pendingId: string) => void;
  onSavePendingToAllRecurring?: (pending: PendingVisit) => void;
  onRemovePending?: (pendingId: string) => void;
  isRecurring?: boolean;
  isLoading?: boolean;
  // Single visit action handlers
  onMarkCompleted?: (visit: Visit) => void;
  onMarkNoShow?: (visit: Visit, withFee?: boolean) => void;
  onMarkCancelled?: (visit: Visit) => void;
  onMarkLateCancelled?: (visit: Visit, withFee?: boolean) => void;
  onResetStatus?: (visit: Visit) => void;
  onDontUsePackage?: (visit: Visit) => void;
  onAttemptPackageDeduction?: (visit: Visit) => void;
  onChargeDefaultPackage?: (visit: Visit) => void;
  onDeleteVisit?: (visit: Visit) => void;
  // New action handlers
  onAddCard?: (visit: Visit) => void;
  onManageNotes?: (visit: Visit) => void;
  onManageRecurringMembership?: (visit: Visit) => void;
  onAssignPackage?: (visit: Visit) => void;
  onChargeProduct?: (visit: Visit) => void;
}

/**
 * VisitsSection - List of visits with management actions
 *
 * Matches v2 modal-new-appointment.hbs lines 661-1125:
 * - "Add Client to Session" autocomplete (inline, v2 lines 664-673)
 * - Pending visits with Save/Remove options (v2 lines 908-914)
 * - Saved visit rows with status, payment info, actions
 */
export const VisitsSection: FC<VisitsSectionProps> = ({
  appointment,
  canManage,
  pendingVisits,
  onAddClient,
  onSavePending,
  onSavePendingToAllRecurring,
  onRemovePending,
  isRecurring = false,
  isLoading = false,
  // Single visit actions
  onMarkCompleted,
  onMarkNoShow,
  onMarkCancelled,
  onMarkLateCancelled,
  onResetStatus,
  onDontUsePackage,
  onAttemptPackageDeduction,
  onChargeDefaultPackage,
  onDeleteVisit,
  // New actions
  onAddCard,
  onManageNotes,
  onManageRecurringMembership,
  onAssignPackage,
  onChargeProduct,
}) => {
  const { t } = useTranslation('translation', { keyPrefix: 'calendar.viewEventDialog.visitsSection' });
  // Keep sorting consistent with backend SQL: status ASC, then user first_name ASC.
  const visits = appointment.visits || [];

  const handleClientSelect = (
    client: ClientSearchResult | ClientSearchResult[] | null
  ) => {
    if (client && !Array.isArray(client)) {
      onAddClient?.(client);
    }
  };

  return (
    <Stack spacing={2}>
      {/* Add Client autocomplete - inline like v2 */}
      {canManage && (appointment.scheduledAppointmentId || appointment.id) && (
        <ClientAutocomplete
          label={t('addClientToSession')}
          placeholder={t('searchForClient')}
          onChange={handleClientSelect}
          value={null}
        />
      )}

      {/* Pending Visits (brandNew in v2) */}
      {pendingVisits.length > 0 && (
        <Stack spacing={1}>
          {pendingVisits.map((pending) => (
            <PendingVisitRow
              key={pending.id}
              pending={pending}
              appointmentId={appointment.id}
              service={appointment.service}
              onSave={() => onSavePending?.(pending.id)}
              onSaveToAllRecurring={() => onSavePendingToAllRecurring?.(pending)}
              onRemove={() => onRemovePending?.(pending.id)}
              isRecurring={isRecurring}
            />
          ))}
        </Stack>
      )}

      {/* Saved Visit List */}
      {visits.length === 0 && pendingVisits.length === 0 ? (
        <Typography color="text.secondary" sx={{ py: 2 }}>
          {t('noVisitsYet')}
        </Typography>
      ) : visits.length > 0 ? (
        <Stack spacing={1}>
          {visits.map((visit) => (
            <VisitRow
              key={visit.id}
              visit={visit}
              appointment={appointment}
              canManage={canManage}
              isRecurring={isRecurring}
              isLoading={isLoading}
              onMarkCompleted={onMarkCompleted}
              onMarkNoShow={onMarkNoShow}
              onMarkCancelled={onMarkCancelled}
              onMarkLateCancelled={onMarkLateCancelled}
              onResetStatus={onResetStatus}
              onDontUsePackage={onDontUsePackage}
              onAttemptPackageDeduction={onAttemptPackageDeduction}
              onChargeDefaultPackage={onChargeDefaultPackage}
              onDeleteVisit={onDeleteVisit}
              onAddCard={onAddCard}
              onManageNotes={onManageNotes}
              onManageRecurringMembership={onManageRecurringMembership}
              onAssignPackage={onAssignPackage}
              onChargeProduct={onChargeProduct}
            />
          ))}
        </Stack>
      ) : null}
    </Stack>
  );
};
